import clsx from 'clsx';
import { ComponentProps, RefObject } from 'react';
import { AltaAppSwitcher } from '@msi-alta/alta-app-switcher';
import opConfig from 'openpathConfig';

import { OpHeader } from 'new-components/OpHeader/OpHeader';
import { UserMenu, UserMenuProps } from 'new-components/UserMenu/UserMenu';
import { OrgSwitcher } from 'new-components/OrgSwitcher/OrgSwitcher';
import type { OrgSwitcherProps } from 'new-components/OrgSwitcher/types';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { HamburgerMenuSvg } from 'components/svgs/HamburgerMenuSvg';
import { AppSwitcher } from 'new-components/AppSwitcher/AppSwitcher';
import { HelpMenu } from 'new-components/HelpMenu/HelpMenu';
import { CobaltChatButton } from 'new-components/CobaltChatButton/CobaltChatButton';
import { useCobaltChatContext } from 'routes/AppContainer';
import { LockdownSelector } from 'new-components/LockdownSelector/LockdownSelector';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { getOpParams } from 'utils/getOpParams';
import { getAccessToken } from 'utils/accessToken';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';

import '@msi-alta/alta-app-switcher/index.css';
import './OpAppHeader.scss';

interface OpAppHeaderProps extends ComponentProps<typeof OpHeader> {
  appSwitcherRef?: RefObject<HTMLSpanElement>;
  homePath?: string;
  onHamburgerClick?: () => void;
  orgSwitcherProps?: OrgSwitcherProps;
  showAppSwitcher?: boolean;
  showOrgSwitcher?: boolean;
  userMenuProps?: UserMenuProps;
  showHamburger?: boolean;
}

export const OpAppHeader = ({
  appSwitcherRef,
  className,
  homePath,
  onHamburgerClick,
  showHamburger,
  orgSwitcherProps,
  showAppSwitcher = true,
  showOrgSwitcher = true,
  userMenuProps,
  ...headerProps
}: OpAppHeaderProps) => {
  const { orgId } = getOpParams();
  const { data: isUnified } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    select: (data) => data.json.data.isUnified,
    suppressErrorMessage: true,
  });

  const isMasterMode = window.location.pathname.includes('/master');
  const { identityId } = useIdentityAndUser();

  const { data: colorTheme, isLoading: isLoadingColorTheme } = useOpQuery({
    apiEndpointName: 'describeIdentityPreferenceSet',
    parameters: [identityId!],
    select: (response) => response.json.data.colorTheme,
  });

  const accessToken = getAccessToken();
  const { isChatOpen, toggleChat } = useCobaltChatContext();

  return (
    <OpHeader className={clsx('op-app-header', className)} {...headerProps}>
      <div className="op-app-header__content">
        <div className="op-app-header__left-content">
          {showHamburger && (
            <OpButton
              className="op-app-header__hamburger"
              onClick={onHamburgerClick}
              icon={<HamburgerMenuSvg />}
              gtm="op-app-header-hamburger"
              data-testid="op-app-header-hamburger"
            />
          )}
          {showOrgSwitcher && (orgSwitcherProps?.visible ?? true) && (
            <OrgSwitcher homePath={homePath} {...orgSwitcherProps} />
          )}
        </div>
        <div className="op-app-header__right-content">
          <LockdownSelector />
          <CobaltChatButton onClick={toggleChat} isActive={isChatOpen} />
          <HelpMenu />
          {showAppSwitcher && (
            <span ref={appSwitcherRef}>
              {/** Need to wait for color theme to load before showing the app switcher or else it will default to dark and will cause a flash of the wrong theme when light mode is selected (when identityId is undefined isLoadingColorTheme will be false and the app switcher will default to dark) */}
              {identityId &&
              !isLoadingColorTheme &&
              (isMasterMode || isUnified) ? (
                <AltaAppSwitcher
                  token={accessToken}
                  orgId={orgId}
                  theme={colorTheme || 'dark'}
                  region={opConfig.APP_SWITCHER_REGION_OVERRIDE || opConfig.ENV}
                />
              ) : (
                <AppSwitcher />
              )}
            </span>
          )}
          <UserMenu {...userMenuProps} />
        </div>
      </div>
    </OpHeader>
  );
};
